import React, { useEffect, useState } from "react";
import { auth, db, storage } from "../firebase";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import { updatePassword, updateEmail, reauthenticateWithCredential, EmailAuthProvider, GoogleAuthProvider } from "firebase/auth";
import { ToastContainer, toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { FaUser, FaShieldAlt, FaPalette, FaBell, FaSignOutAlt, FaChevronRight, FaDownload, FaGoogle } from "react-icons/fa";
import styles from "../styles/Settings.module.css";
import { ScrollReveal } from "../hooks/useScrollAnimation";

export default function SettingsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("profile");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Data States
  const [userData, setUserData] = useState({
    username: "",
    email: "",
    bio: "",
    avatar: "",
    location: "",
    website: "",
    accentColor: "#D00000", // Default Blood Red
    notifications: {
      email: true,
      push: true,
      mentions: true
    }
  });

  // Security States
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isGoogleAuth, setIsGoogleAuth] = useState(false);

  // Avatar State
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");

  useEffect(() => {
    const fetchSettings = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/login");
        return;
      }

      // Check auth provider
      const isGoogle = user.providerData.some(p => p.providerId === GoogleAuthProvider.PROVIDER_ID);
      setIsGoogleAuth(isGoogle);

      try {
        const docRef = doc(db, "users", user.uid);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setUserData(prev => ({
            ...prev,
            username: data.username || "",
            email: user.email || "",
            bio: data.bio || "",
            avatar: data.avatar || "",
            location: data.location || "",
            website: data.website || "",
            accentColor: data.accentColor || "#D00000",
            notifications: { ...prev.notifications, ...data.notifications }
          }));
          setAvatarPreview(data.avatar || "");

          // Apply initial theme settings
          if (data.accentColor) {
            document.documentElement.style.setProperty('--color-primary', data.accentColor);
            document.documentElement.style.setProperty('--color-primary-hover', adjustColor(data.accentColor, 20));
          }
        }
      } catch (err) {
        toast.error(`Failed to load settings: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, [navigate]);

  // Helper to lighten/darken color
  const adjustColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
  }

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const uploadAvatar = async () => {
    if (!avatarFile) return null;
    try {
      const uid = auth.currentUser?.uid;
      const storageRef = ref(storage, `avatars/${uid}/${avatarFile.name}`);
      const snapshot = await uploadBytes(storageRef, avatarFile);
      return await getDownloadURL(snapshot.ref);
    } catch (err) {
      toast.error(`Avatar upload failed: ${err.message}`);
      return null;
    }
  };

  const handleSave = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return;

    setIsSaving(true);
    try {
      const avatarUrl = await uploadAvatar();
      const finalAvatar = avatarUrl || userData.avatar;

      await setDoc(doc(db, "users", uid), {
        ...userData,
        avatar: finalAvatar,
        updatedAt: new Date()
      }, { merge: true });

      if (!isGoogleAuth && userData.email !== auth.currentUser.email) {
        await updateEmail(auth.currentUser, userData.email);
      }

      // Apply Accent Color Globally
      document.documentElement.style.setProperty('--color-primary', userData.accentColor);
      document.documentElement.style.setProperty('--color-primary-hover', adjustColor(userData.accentColor, 20));

      toast.success("Settings saved successfully!");
      setUserData(prev => ({ ...prev, avatar: finalAvatar }));
      setAvatarFile(null);
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAccentChange = (color) => {
    setUserData(prev => ({ ...prev, accentColor: color }));
    // Live preview
    document.documentElement.style.setProperty('--color-primary', color);
  };

  const handlePasswordReset = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setOpenPasswordDialog(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Password updated successfully!");
    } catch (err) {
      toast.error(`Password update failed: ${err.message}`);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      const user = auth.currentUser;
      // Re-auth based on provider
      if (!isGoogleAuth) {
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
      }
      // Note: Google re-auth flow is complex to implement fully inline without a popup, 
      // but for this implementation we'll assume session is fresh or handle errors.

      await deleteDoc(doc(db, "users", user.uid));
      await user.delete();
      toast.success("Account deleted successfully");
      navigate("/");
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        toast.error("Please log out and log back in to delete your account.");
      } else {
        toast.error(`Account deletion failed: ${err.message}`);
      }
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleDownloadData = () => {
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(userData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "staco_user_data.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  if (isLoading) return <div className={styles.container} style={{ textAlign: 'center', marginTop: '50px' }}>Loading settings...</div>;

  const renderContent = () => {
    switch (activeTab) {
      case "profile":
        return (
          <div className={styles.panel}>
            <h2 className={styles.sectionTitle}>Edit Profile</h2>

            <div className={styles.avatarSection}>
              <img src={avatarPreview || "https://via.placeholder.com/150"} alt="Avatar" className={styles.avatarPreview} />
              <div>
                <label className={styles.uploadBtn}>
                  Change Avatar
                  <input type="file" className={styles.fileInput} accept="image/*" onChange={handleFileChange} />
                </label>
                <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '8px' }}>Max size 2MB</p>
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Display Name</label>
              <input
                className={styles.input}
                value={userData.username}
                onChange={(e) => setUserData({ ...userData, username: e.target.value })}
                placeholder="Your name"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Bio</label>
              <textarea
                className={styles.textarea}
                value={userData.bio}
                onChange={(e) => setUserData({ ...userData, bio: e.target.value })}
                placeholder="Tell us a bit about yourself..."
              />
            </div>

            <div className={styles.gridRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Location</label>
                <input
                  className={styles.input}
                  value={userData.location}
                  onChange={(e) => setUserData({ ...userData, location: e.target.value })}
                  placeholder="City, Country"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Website</label>
                <input
                  className={styles.input}
                  value={userData.website}
                  onChange={(e) => setUserData({ ...userData, website: e.target.value })}
                  placeholder="https://"
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Email Address</label>
              <input
                className={styles.input}
                value={userData.email}
                onChange={(e) => setUserData({ ...userData, email: e.target.value })}
                disabled={isGoogleAuth}
              />
              {isGoogleAuth && <p className={styles.helperText}>Managed by Google</p>}
            </div>

            <button className={styles.btnPrimary} onClick={handleSave} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Profile"}
            </button>
          </div>
        );

      case "appearance":
        return (
          <div className={styles.panel}>
            <h2 className={styles.sectionTitle}>Appearance</h2>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <h4>Accent Color</h4>
                <p>Customize your interface color</p>
              </div>
              <div className={styles.colorPickerWrapper}>
                <input
                  type="color"
                  value={userData.accentColor}
                  onChange={(e) => handleAccentChange(e.target.value)}
                  className={styles.colorWheel}
                />
                <span className={styles.colorValue}>{userData.accentColor}</span>
              </div>
            </div>

            <button className={styles.btnPrimary} onClick={handleSave} disabled={isSaving}>
              Save Appearance
            </button>
          </div>
        );

      case "notifications":
        return (
          <div className={styles.panel}>
            <h2 className={styles.sectionTitle}>Notification Preferences</h2>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <h4>Email Notifications</h4>
                <p>Receive emails about your account and activity</p>
              </div>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  className={styles.toggleInput}
                  checked={userData.notifications.email}
                  onChange={(e) => setUserData({
                    ...userData,
                    notifications: { ...userData.notifications, email: e.target.checked }
                  })}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <h4>Push Notifications</h4>
                <p>Receive notifications on your device</p>
              </div>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  className={styles.toggleInput}
                  checked={userData.notifications.push}
                  onChange={(e) => setUserData({
                    ...userData,
                    notifications: { ...userData.notifications, push: e.target.checked }
                  })}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <h4>Mentions & Replies</h4>
                <p>Notify when someone mentions or replies to you</p>
              </div>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  className={styles.toggleInput}
                  checked={userData.notifications.mentions}
                  onChange={(e) => setUserData({
                    ...userData,
                    notifications: { ...userData.notifications, mentions: e.target.checked }
                  })}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            <button className={styles.btnPrimary} onClick={handleSave} disabled={isSaving}>
              Save Preferences
            </button>
          </div>
        );

      case "security":
        return (
          <div className={styles.panel}>
            <h2 className={styles.sectionTitle}>Account & Security</h2>

            {isGoogleAuth ? (
              <div className={styles.googleInfoBox}>
                <FaGoogle className={styles.googleIcon} />
                <div>
                  <h4>Connected with Google</h4>
                  <p>Your account is secured by Google. Manage your password and security settings through your Google Account.</p>
                </div>
              </div>
            ) : (
              <div className={styles.settingItem}>
                <div className={styles.settingInfo}>
                  <h4>Change Password</h4>
                  <p>Update your password regularly for security</p>
                </div>
                <button className={styles.btnSecondary} onClick={() => setOpenPasswordDialog(true)}>
                  Update
                </button>
              </div>
            )}

            <div className={styles.settingItem} style={{ marginTop: '20px' }}>
              <div className={styles.settingInfo}>
                <h4>Your Data</h4>
                <p>Download a copy of your personal data</p>
              </div>
              <button className={styles.btnSecondary} onClick={handleDownloadData}>
                <FaDownload /> Download JSON
              </button>
            </div>

            <div className={styles.dangerZone} style={{ marginTop: '40px' }}>
              <h4 className={styles.dangerTitle}>Danger Zone</h4>
              <p className={styles.modalText}>
                Deleting your account is permanent. All your data will be wiped.
              </p>
              <button className={styles.btnDanger} onClick={() => setOpenDeleteDialog(true)}>
                Delete Account
              </button>
            </div>
          </div>
        );

      default: return null;
    }
  };

  return (
    <div className={styles.container}>
      <ToastContainer position="top-right" theme="dark" />

      <div className={styles.header}>
        <h1 className={styles.title}>Settings</h1>
        <p className={styles.subtitle}>Manage your account, appearance, and security preferences</p>
      </div>

      <div className={styles.content}>
        {/* Sidebar */}
        <ScrollReveal animation="animate-slide-in-right" className={styles.sidebar}>
          <button
            className={`${styles.navItem} ${activeTab === 'profile' ? styles.activeNav : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <FaUser /> Profile
          </button>
          <button
            className={`${styles.navItem} ${activeTab === 'appearance' ? styles.activeNav : ''}`}
            onClick={() => setActiveTab('appearance')}
          >
            <FaPalette /> Appearance
          </button>
          <button
            className={`${styles.navItem} ${activeTab === 'notifications' ? styles.activeNav : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            <FaBell /> Notifications
          </button>
          <button
            className={`${styles.navItem} ${activeTab === 'security' ? styles.activeNav : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <FaShieldAlt /> Security
          </button>
          <div style={{ flexGrow: 1 }}></div>
          <button
            className={styles.navItem}
            onClick={() => navigate("/")}
            style={{ color: 'var(--text-secondary)', borderTop: '1px solid var(--border-color)', marginTop: 'auto', paddingTop: '16px' }}
          >
            <FaChevronRight /> Back to Home
          </button>
        </ScrollReveal>

        {/* Main Content */}
        <ScrollReveal key={activeTab} animation="animate-fade-in-up" style={{ flex: 1 }}>
          {renderContent()}
        </ScrollReveal>
      </div>

      {/* Password Reset Modal */}
      {openPasswordDialog && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Change Password</h3>
            <p className={styles.modalText}>Enter your current password and a new one.</p>

            <div className={styles.formGroup}>
              <label className={styles.label}>Current Password</label>
              <input type="password" className={styles.input} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>New Password</label>
              <input type="password" className={styles.input} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>Confirm Password</label>
              <input type="password" className={styles.input} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
            </div>

            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setOpenPasswordDialog(false)}>Cancel</button>
              <button className={styles.btnPrimary} style={{ marginTop: 0, width: 'auto' }} onClick={handlePasswordReset}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {openDeleteDialog && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle} style={{ color: 'var(--color-error)' }}>Delete Account?</h3>
            <p className={styles.modalText}>This action is irreversible. All your data will be wiped.</p>

            {!isGoogleAuth && (
              <div className={styles.formGroup}>
                <label className={styles.label}>Password</label>
                <input type="password" className={styles.input} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
              </div>
            )}

            <div className={styles.modalActions}>
              <button className={styles.btnSecondary} onClick={() => setOpenDeleteDialog(false)}>Cancel</button>
              <button className={styles.btnDanger} onClick={handleDeleteAccount}>Delete Forever</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}